import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Expand } from 'lucide-react';

const VideoShowcase: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      setHasInteracted(true);
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        // Reset to beginning, unmute, and play
        videoRef.current.currentTime = 0;
        videoRef.current.muted = false;
        setIsMuted(false);
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div
            className="text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-700"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            }}
          >
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 font-serif">
              Experience the power of AI-driven policy intelligence
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-[#102A43] mb-4 sm:mb-6">
              See LegisIQ in Action
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
              Watch our 2-minute platform overview to see how LegisIQ's unified platform and AI
              driven tools transform legislative tracking from hours of manual work to minutes of
              automated intelligence.
            </p>
          </div>

          {/* Video Container with Expanded View */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-700 delay-200 group"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              aspectRatio: '16/9',
              background: 'linear-gradient(135deg, #102A43 0%, #1a237e 100%)',
            }}
          >
            {/* Video Element - always visible but starts at 1:12 for thumbnail */}
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src="/Final_compressed.mp4#t=72"
              muted={isMuted}
              loop
              playsInline
              controls={false}
              preload="metadata"
              onClick={togglePlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            >
              Your browser does not support the video tag.
            </video>

            {/* Overlay with Play Button - shows before interaction */}
            {!hasInteracted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <div className="relative text-center text-white z-10 mt-32 sm:mt-48 md:mt-56">
                  <button
                    onClick={togglePlay}
                    className="inline-flex items-center gap-2 sm:gap-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm border-2 border-white/50 text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 transition-all hover:scale-105 shadow-2xl"
                  >
                    <Play className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span className="font-semibold text-base sm:text-lg">Watch Demo</span>
                  </button>
                </div>
              </div>
            )}

            {/* Video Controls - Only show after interaction */}
            {hasInteracted && (
              <>
                {/* Gradient Overlay for controls visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Play/Pause Button */}
                <button
                  onClick={togglePlay}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#102A43] rounded-full p-4 sm:p-6 shadow-2xl transition-all hover:scale-110 backdrop-blur-sm opacity-0 group-hover:opacity-100"
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 sm:w-8 sm:h-8" />
                  ) : (
                    <Play className="w-6 h-6 sm:w-8 sm:h-8 ml-0.5 sm:ml-1" />
                  )}
                </button>

                {/* Control Bar */}
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {/* Left side - Status Badge */}
                  <div className="bg-[#102A43]/90 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                    <span className="hidden sm:inline">{isPlaying ? 'Playing' : 'Paused'} • </span>
                    Platform Demo
                  </div>

                  {/* Right side - Controls */}
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      onClick={toggleMute}
                      className="bg-white/90 hover:bg-white text-[#102A43] rounded-full p-2 sm:p-3 shadow-lg transition-all hover:scale-110 backdrop-blur-sm"
                      aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                    >
                      {isMuted ? (
                        <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
                      ) : (
                        <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                    </button>
                    <button
                      onClick={handleFullscreen}
                      className="bg-white/90 hover:bg-white text-[#102A43] rounded-full p-2 sm:p-3 shadow-lg transition-all hover:scale-110 backdrop-blur-sm"
                      aria-label="Fullscreen"
                    >
                      <Expand className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;
